import { Button, TextArea } from '@blueprintjs/core';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import {
  showSaveSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import { getOrgNameFromId } from 'Data/context/Organizations';
import ResourceData from 'Data/ResourceData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useValidation } from 'Lib/hooks/useValidation';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { formatDate } from 'Util/dateUtil';
import {
  InventorySearchButton,
  InventoryTagLabelAndLink,
} from './InventoryLinks';

let DM = new DataManager();

const ResourceInformationPanel = ({
  resourceId,
  resourceObject,
  title = 'Details',
  editable = false,
  collapsible = true,
  onResourceSaved = () => {},
  ...props
}) => {
  const {
    currentObject: resource,
    hasBeenUpdated,
    reset: resetResource,
    updateField,
    updateFields,
    originalObject: originalResource,
    doSave,
  } = useUpdatableObject(ResourceData.updateRecord, ResourceData.createRecord);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalResource,
    undoFunction: ResourceData.updateRecord,
    onUndo: (originalResource) => fetchResource(originalResource.id),
    objectType: 'Resource',
  });

  const [editState, setEditState] = useState(undefined);
  const [showInvLookup, setShowInvLookup] = useState(true);

  useEffect(() => {
    DM.add(
      ResourceData.registerListener('BLANK_RECORD_LOADED', onResourceLoaded),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_LOADED', onResourceLoaded),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_CREATED', onResourceCreated),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('RECORD_UPDATED', onResourceUpdated),
      ResourceData,
    );
    DM.add(
      ResourceData.registerListener('ASSET_DETAILS_RETRIEVED', onAssetSearched),
      ResourceData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  let fields = {
    name: {
      label: 'Name',
      onEdit: (newValue) => {
        updateField('name', newValue);
      },
      isRequired: true,
    },

    parent_organization_id: {
      label: 'Core Facility',
      isRequired: true,
      isNumber: true,
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <OrganizationSelect
              isRequired
              defaultSelectedId={resource.parent_organization_id}
              onSelectOrganization={(org) =>
                updateField('parent_organization_id', org?.id || null)
              }
              onBlur={blurField}
            />
          ) : (
            getOrgNameFromId(resource.parent_organization_id)
          );
        },
      },
    },

    description: {
      label: 'Description',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <TextArea
              fill
              autoResize
              value={resource.description || ''}
              onChange={(e) => {
                updateField('description', e.target.value);
              }}
            />
          ) : (
            <div
              style={{
                whiteSpace: 'pre-line',
              }}>
              {resource.description}
            </div>
          );
        },
      },
    },

    inventory_tag_number: {
      label: 'Inventory Tag Number',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <TextArea
              fill
              autoResize
              value={resource.inventory_tag_number || ''}
              onChange={(e) => {
                updateField('inventory_tag_number', e.target.value);
              }}
            />
          ) : (
            resource?.inventory_tag_number && (
              <InventoryTagLabelAndLink
                inventory_tag_number={resource.inventory_tag_number}
              />
            )
          );
        },
      },
    },
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, resource);

  useEffect(() => {
    if (resourceId && resourceId >= 0) {
      fetchResource(resourceId);
    }
  }, [resourceId]);

  useEffect(() => {
    if (resourceObject) {
      resetResource(resourceObject);
    } else if (resourceObject?.id && Number(resourceObject.id) >= 0) {
      fetchResource(resourceObject.id);
    }
  }, [resourceObject]);

  const onResourceLoaded = (payload) => {
    if (
      (!resourceId && !resourceObject?.id) ||
      resourceId === payload.id ||
      resourceObject?.id === payload.id
    ) {
      resetResource(payload);
      payload?.id && resetUndo(() => fetchResource(payload.id));
      resetValidation();
    }
  };

  const fetchResource = (id) => {
    ResourceData.getResourceAndAssociations(id);
  };

  const onResourceCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Resource Created');
      fetchResource(payload.id);
      setEditState(false);
      onResourceSaved(payload);
    }
  };

  const onResourceUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
      onResourceSaved(payload);
    }
  };

  const onAssetSearched = (payload) => {
    if (payload?.status === 'success') {
      updateFields({
        name: payload.asset_name,
        inventory_tag_number: payload.asset_tag_number,
        description:
          `Details pulled from inventory system on ${formatDate(
            new Date(),
          )}.\n` +
          // `Org Code: ${payload.organization_code}\n` +
          `Org Name: ${payload.organization_name}\n`,
      });
      setShowInvLookup(false);
      resetValidation();
    } else {
      showToast(
        `No asset found for tag number: ${payload.tag_number}`,
        'danger',
      );
    }
  };
  const undoAction = () => {
    fetchResource(resource.id);
    resetValidation();
  };

  const undoButton = resource?.id && hasBeenUpdated && (
    <UndoButton clickHandler={undoAction} />
  );
  const saveButton = hasBeenUpdated && <ValidatedSaveButton doSave={doSave} />;

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  const closeInvLookupButton = (
    <Button
      small
      minimal
      intent='danger'
      icon={'delete'}
      onClick={() => {
        setShowInvLookup(false);
      }}
    />
  );

  const getPanelContent = (inEditState) => {
    return (
      <>
        {!resource?.id && showInvLookup && (
          <InventorySearchButton closeButton={closeInvLookupButton} />
        )}
        {getValidatedDetailTable(inEditState)}
      </>
    );
  };

  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible={collapsible}
      renderChild={getPanelContent}
      parentEditState={editState}
      {...props}
    />
  );
};

ResourceInformationPanel.propTypes = {
  resourceId: PropTypes.number,
  resourceObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  collapsible: PropTypes.bool,
  onResourceSaved: PropTypes.func,
};

export default ResourceInformationPanel;
